import React, { useMemo } from 'react'
import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  makeStyles,
  Typography,
  useTheme
} from '@material-ui/core'
import { styles } from './infoCard.styles'
import { HomeOutlined } from '@material-ui/icons'
import ButtonWithPermissions from '../../../../../components/buttonWithPermissions/buttonWithPermissions'
import { LoadingCard } from '../../../../../components/shared/loadingCard/loadingCard'
import { Resources } from '../../../../../model/RBAC/constants'
import { UserPermission } from '@globaleris/ui.library'
import { hasPermission } from '../../../../../helpers/permissionsHelper'
import { CardWithTransition } from '../../../../../components/shared/cardWithTransition/CardWithTransition'
import { EmptyContent } from '../shared/emptyCard'
import { useTranslation } from 'react-i18next'
import { Header } from '../shared/header'

export interface InfoCardProps {
  isFetching: boolean
  mainTitle?: string
  subtitle?: string
  texts?: string[]
  error?: boolean
  onClick: () => void
  onRetry: () => void
}

export const InfoCard: React.FC<InfoCardProps> = ({
  isFetching,
  mainTitle,
  subtitle,
  texts,
  error,
  onClick,
  onRetry
}) => {
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const [t] = useTranslation(['globalerisLibrary', 'clientFile'])

  const hasPermissionClientOrExtendedEdit =
    hasPermission(Resources.Client, [UserPermission.EDIT]) ||
    hasPermission(Resources.ExtendedClients, [UserPermission.EDIT])

  if (isFetching) {
    return <LoadingCard />
  }

  const ClientInfoHeader = () =>
    React.useMemo(() => {
      return <Header avatarIcon={<HomeOutlined />} title={mainTitle ?? ''} titleVariant="primary" />
    }, [mainTitle])

  if (error) {
    return (
      <CardWithTransition show={true} raised>
        <ClientInfoHeader />
        <EmptyContent title={t('clientFile:components.infoCard.errorCard.errorMessage')}>
          <ButtonWithPermissions
            hasPermission={true}
            size="small"
            color="primary"
            variant="contained"
            onClick={onRetry}
          >
            {t('clientFile:components.infoCard.errorCard.buttonLabel')}
          </ButtonWithPermissions>
        </EmptyContent>
      </CardWithTransition>
    )
  }

  return (
    <CardWithTransition show={true} raised style={{ display: 'flex', flexDirection: 'column' }}>
      <ClientInfoHeader />
      <CardContent className={classes.cardContent}>
        <Typography variant="subtitle1">{subtitle}</Typography>
        {texts && texts.length > 0
          ? texts.map((text, idx) => (
              <Typography key={`${text}-${idx}`} variant="body2" className={classes.description}>
                {text}
              </Typography>
            ))
          : ''}
      </CardContent>

      <CardActions className={classes.cardActionsRight}>
        <ButtonWithPermissions
          size="small"
          color="primary"
          hasPermission={hasPermissionClientOrExtendedEdit}
          onClick={onClick}
        >
          {t('clientFile:components.infoCard.actions.edit')}
        </ButtonWithPermissions>
      </CardActions>
    </CardWithTransition>
  )
}
